import { useEffect } from "react";
import { Navigate, useLocation } from "react-router";
import { useAppSelector, useAppDispatch } from "@/store/hooks";
import {
  selectIsAuthenticated,
  selectToken,
  validateSession,
} from "@/store/slices/authSlice";
import { tokenNeedsRefresh } from "@/lib/tokenUtils";

export interface ProtectedRouteProps {
  children: React.ReactNode;
}

/**
 * ProtectedRoute component for protecting authenticated routes
 *
 * Story 4.9 Enhancement: Checks token expiry locally on route changes.
 * Only calls validateSession() if token is expired or expiring soon (< 5 hours).
 * Zero latency for valid tokens - no API calls unless refresh is needed.
 *
 * @example
 * ```tsx
 * <Route
 *   path="/trophy-room"
 *   element={
 *     <ProtectedRoute>
 *       <TrophyRoomPage />
 *     </ProtectedRoute>
 *   }
 * />
 * ```
 */
export const ProtectedRoute = ({ children }: ProtectedRouteProps) => {
  const dispatch = useAppDispatch();
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const token = useAppSelector(selectToken);
  const location = useLocation();

  // Check token expiry on route change (Story 4.9 fix)
  // Only dispatch validateSession() if token needs refresh
  useEffect(() => {
    if (isAuthenticated && tokenNeedsRefresh(token)) {
      dispatch(validateSession());
    }
  }, [dispatch, isAuthenticated, token, location.pathname]);

  if (!isAuthenticated) {
    // Redirect to login with return URL (preserves full path + search params)
    const returnUrl = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/auth/login?returnUrl=${returnUrl}`} replace />;
  }

  return <>{children}</>;
};
